import { useCallback, useEffect, useState } from 'react'
import { api, dropNulls } from './api'
import type { FertilizerLog } from './types'

export type CreateFertilizerLogInput = {
  fieldId: string
  seasonYear: number
  appliedOn: string
  blend: string
  rateAmount?: number | null
  rateUnit?: string | null
  lotNumber?: string | null
  applicatorName?: string | null
  notes?: string | null
}

export type UpdateFertilizerLogInput = Partial<Omit<CreateFertilizerLogInput, 'fieldId' | 'seasonYear'>>

export function useFertilizerLogs(
  orgId: string | undefined,
  farmId: string | undefined,
  fieldId: string | undefined,
  seasonYear: number,
) {
  const [logs, setLogs] = useState<FertilizerLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const base = orgId && farmId ? `/orgs/${orgId}/farms/${farmId}/fertilizer-logs` : null

  const loadLogs = useCallback(async () => {
    if (!base || !fieldId) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.getPage<FertilizerLog>(`${base}?fieldId=${fieldId}&seasonYear=${seasonYear}`)
      setLogs(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load fertilizer logs.')
    } finally {
      setLoading(false)
    }
  }, [base, fieldId, seasonYear])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  async function addLog(input: CreateFertilizerLogInput) {
    if (!base) return
    const created = await api.post<FertilizerLog>(base, dropNulls(input))
    setLogs((prev) => [created, ...prev])
    return created
  }

  async function updateLog(id: string, patch: UpdateFertilizerLogInput) {
    if (!base) return
    const updated = await api.patch<FertilizerLog>(`${base}/${id}`, patch)
    setLogs((prev) => prev.map((l) => (l.id === id ? updated : l)))
    return updated
  }

  return { logs, loading, error, addLog, updateLog }
}
